import React, { useEffect, useRef, useState } from "react";
import AuthPasswordLayout, {
  PanelIllustration,
} from "../../../components/AuthPasswordLayout";
import { useRecaptchaV2, useRecaptchaV3 } from "../../../../../hooks/useRecaptcha";
import { identifyLogin, login } from "../../../_redux/authCrud";
import { FIRST_ACCESS_ROUTES } from "../../../../../pages/FirstAccess/routes";
import useUnleashFeatureFlagAdapter from "../../../../../adapter/UnleashFeatureFlagAdapter";
import { FEATURE_FLAGS } from "../../../../../constants/FeatureFlag";
import { config } from "../../../../../../config/env";
import {
  trackLoginAttemptFailed,
  trackLoginAttemptStarted,
  trackLoginAttemptSuccessful,
  trackLoginPageDisplayed,
} from "../../../trackLoginEvents";
import {
  AuthStatus,
  LOGIN_STEP_ILLUSTRATION,
  LoginIdentifyStatus,
  LoginStep,
  NEW_AUTH_SERVICE_APP_NAME,
  type LoginStepValue,
} from "./constants";
import EmailStepForm from "./components/EmailStepForm";
import LoginFooter from "./components/LoginFooter";
import PasswordNotCreatedMessage from "./components/PasswordNotCreatedMessage";
import PasswordStepForm from "./components/PasswordStepForm";
import * as S from "./styles";

type NewLoginProps = {
  history: {
    push: (path: string, state?: Record<string, unknown>) => void;
  };
  login: (token: string) => void;
  setUserLoginId: (loginId: number | null) => void;
};

type LoginError = {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
};

const resolveLoginErrorMessage = (err: LoginError): string =>
  err?.response?.data?.message ??
  "Não foi possível entrar agora. Verifique seus dados e tente novamente.";

export default function NewLogin({
  history,
  login: storeLogin,
  setUserLoginId,
}: NewLoginProps): React.ReactElement {
  const refreshRecaptchaV3Ref = useRef<(() => void) | undefined>();
  const [handleRecaptchaV2Callback, captchaV2Params] = useRecaptchaV2();
  const [handleRecaptchaV3Callback, captchaV3Params] = useRecaptchaV3(
    refreshRecaptchaV3Ref
  );
  const { rolloutReleaseByContextDirectly } = useUnleashFeatureFlagAdapter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<LoginStepValue>(LoginStep.EMAIL);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [enableRecaptchaV2, setEnableRecaptchaV2] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaVersion, setRecaptchaVersion] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    trackLoginPageDisplayed();
  }, []);

  useEffect(() => {
    if (!captchaV3Params.v3Token) {
      return;
    }

    setRecaptchaToken(captchaV3Params.v3Token);
    setRecaptchaVersion("3");
  }, [captchaV3Params.v3Token]);

  useEffect(() => {
    if (!captchaV2Params.v2Token) {
      return;
    }

    setRecaptchaToken(captchaV2Params.v2Token);
    setRecaptchaVersion("2");
  }, [captchaV2Params.v2Token]);

  useEffect(() => {
    captchaV3Params.v3Retrieving
      ? setDisableButton(true)
      : setDisableButton(false);
  }, [captchaV3Params.v3Retrieving]);

  useEffect(() => {
    if (!enableRecaptchaV2) {
      return;
    }

    captchaV2Params.v2Token ? setDisableButton(false) : setDisableButton(true);
  }, [enableRecaptchaV2, captchaV2Params.v2Token]);

  const handleInvalidRecaptcha = (): boolean => {
    if (!enableRecaptchaV2 && refreshRecaptchaV3Ref.current) {
      refreshRecaptchaV3Ref.current();
      setEnableRecaptchaV2(true);
      setDisableButton(true);
      return true;
    }

    if (enableRecaptchaV2) {
      setError(
        "Ocorreu um erro na validação do reCAPTCHA. Por favor, tente novamente."
      );
      return true;
    }

    return false;
  };

  const redirectToNewAuthService = (targetEmail: string): void => {
    const params = new URLSearchParams({
      client_id: config.hydra.clientId,
      audience: config.hydra.audience,
      scope: config.hydra.scope,
      login_hint: targetEmail,
    });

    window.location.assign(`${config.authFront.url}/oauth/start?${params.toString()}`);
  };

  const handleIdentify = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();

    if (!email.trim()) {
      setError("Informe seu e-mail para continuar.");
      return;
    }

    const targetEmail = email.trim();

    if (config.authFront.url) {
      const useNewAuthService = await rolloutReleaseByContextDirectly(
        FEATURE_FLAGS.RELEASE.ENABLE_NEW_AUTH_SERVICE,
        { email: targetEmail, group: NEW_AUTH_SERVICE_APP_NAME }
      );

      if (useNewAuthService) {
        setLoading(true);
        setDisableButton(true);
        redirectToNewAuthService(targetEmail);
        return;
      }
    }

    if (!recaptchaToken || !recaptchaVersion) {
      setError("Aguarde a validação de segurança e tente novamente.");
      return;
    }

    setError("");
    setLoading(true);
    setDisableButton(true);

    try {
      const { data: responseData } = await identifyLogin(
        targetEmail,
        recaptchaToken,
        recaptchaVersion
      );
      const identifyData = responseData?.data;
      const status = identifyData?.status;

      if (status === LoginIdentifyStatus.FIRST_ACCESS_PENDING) {
        const firstAccessEmail = identifyData?.email || targetEmail;

        if (identifyData?.accessToken) {
          history.push(FIRST_ACCESS_ROUTES.VERIFICATION, {
            email: firstAccessEmail,
            tempToken: identifyData.accessToken,
          });
          return;
        }

        const firstAccessState: {
          email: string;
          retryAt?: string | Date;
        } = { email: firstAccessEmail };

        if (identifyData?.retryAt) {
          firstAccessState.retryAt = identifyData.retryAt;
        }

        history.push(FIRST_ACCESS_ROUTES.BASE, firstAccessState);
        return;
      }

      setEnableRecaptchaV2(false);

      if (status === LoginIdentifyStatus.PASSWORD_NOT_CREATED) {
        setStep(LoginStep.PASSWORD_NOT_CREATED);
        return;
      }

      setStep(LoginStep.PASSWORD);
    } catch (err) {
      const loginError = err as LoginError;
      setDisableButton(false);

      if (
        loginError?.response?.data?.error === "INVALID_RECAPTCHA" &&
        handleInvalidRecaptcha()
      ) {
        return;
      }

      setError(
        loginError?.response?.data?.message ??
          "Não foi possível continuar agora. Tente novamente em instantes."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();

    if (!password) {
      setError("Informe sua senha para entrar.");
      return;
    }

    setError("");
    setLoading(true);
    setDisableButton(true);
    trackLoginAttemptStarted();

    try {
      const { data: responseData } = await login(
        email.trim(),
        password,
        recaptchaToken,
        recaptchaVersion
      );

      const isTwoFactorRequired =
        responseData.status === AuthStatus.REQUIRED_2FA;
      const hasMissingTwoFactorCredentials =
        !responseData.userLoginId || !responseData.accessToken;

      if (isTwoFactorRequired && hasMissingTwoFactorCredentials) {
        trackLoginAttemptFailed();
        setError("Erro ao processar autenticação de dois fatores.");
        return;
      }

      if (isTwoFactorRequired) {
        trackLoginAttemptSuccessful();
        history.push("/auth/two-factor", {
          email: email.trim(),
          userId: responseData.userLoginId,
          tempToken: responseData.accessToken,
        });
        return;
      }

      const { accessToken, userLoginId } = responseData;
      trackLoginAttemptSuccessful();
      setUserLoginId(userLoginId);
      storeLogin(accessToken);
    } catch (err) {
      const loginError = err as LoginError;
      trackLoginAttemptFailed();
      setPassword("");
      setDisableButton(false);

      if (
        loginError?.response?.data?.error === "INVALID_RECAPTCHA" &&
        handleInvalidRecaptcha()
      ) {
        return;
      }

      setError(resolveLoginErrorMessage(loginError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPasswordLayout
      rightContent={
        <PanelIllustration src={LOGIN_STEP_ILLUSTRATION[step]} alt="" />
      }
    >
      <S.LoginContainer>
        {step === LoginStep.PASSWORD_NOT_CREATED ? (
          <PasswordNotCreatedMessage email={email} />
        ) : (
          <>
            <S.Title>Boas-vindas de volta!</S.Title>

            {step === LoginStep.PASSWORD ? (
              <PasswordStepForm
                email={email}
                password={password}
                error={error}
                loading={loading}
                disableButton={disableButton}
                showPassword={showPassword}
                enableRecaptchaV2={enableRecaptchaV2}
                onPasswordChange={setPassword}
                onTogglePassword={() => setShowPassword((prev) => !prev)}
                onSubmit={handleLogin}
                recaptchaV2Callback={handleRecaptchaV2Callback}
                recaptchaV3Callback={handleRecaptchaV3Callback}
              />
            ) : (
              <EmailStepForm
                email={email}
                error={error}
                loading={loading}
                disableButton={disableButton}
                enableRecaptchaV2={enableRecaptchaV2}
                onEmailChange={setEmail}
                onSubmit={handleIdentify}
                recaptchaV2Callback={handleRecaptchaV2Callback}
                recaptchaV3Callback={handleRecaptchaV3Callback}
              />
            )}

            <LoginFooter />
          </>
        )}
      </S.LoginContainer>
    </AuthPasswordLayout>
  );
}
